import {
  Stack,
  Button,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { tokens } from "../../../../../theme";

type activeTabType =
  | "tribute-comments"
  | "youtube-comments"
  | "contacts"
  | "media"
  | "upload"
  | "delivery"
  | "handwritten-comments";

interface Props {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<activeTabType>>;
  tributeCommentsCount: number;
  youtubeCommentsCount: number;
  contactsCount: number;
  mediaCount: number;
  handwrittenCommentsCount: number;
}

const TAB_MOBILE_LABELS: Record<string, string> = {
  "tribute-comments": "Tributes",
  "youtube-comments": "YouTube",
  "contacts": "Contacts",
  "media": "Media",
  "upload": "Upload",
  "delivery": "Delivery",
  "handwritten-comments": "Handwritten",
};

export const TabButtons: React.FC<Props> = ({
  activeTab,
  setActiveTab,
  tributeCommentsCount,
  youtubeCommentsCount,
  contactsCount,
  mediaCount,
  handwrittenCommentsCount,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isTabButtonActive = (button: string) => {
    if (button === activeTab) {
      return `3px solid ${reduxTheme.primary_colors[400]}`;
    }
  };

  const buttonSx = (tab: string) => ({
    fontWeight: "bold",
    borderBottom: isTabButtonActive(tab),
    borderRadius: "0 !important",
    pb: 1,
    flexShrink: 0,
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    px: { xs: 1, sm: 2 },
    minWidth: "unset",
  });

  const label = (full: string, tab: string) =>
    isMobile ? TAB_MOBILE_LABELS[tab] : full;

  return (
    <Box
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Stack
        direction={"row"}
        gap={2}
        sx={{
          borderBottom: `1px solid ${reduxTheme.primary_colors[300]}`,
          minWidth: "max-content",
        }}
      >
        {/* Tribute Comments */}
        <Button
          disableElevation
          sx={buttonSx("tribute-comments")}
          onClick={() => setActiveTab("tribute-comments")}
          variant="text"
        >
          {label("Tribute Comments", "tribute-comments")}
          <Typography
            variant="body2"
            sx={{
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              color: "#fff",
              display: "grid",
              placeContent: "center",
              ml: 1,
              backgroundColor: "#048b3dff",
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              flexShrink: 0,
            }}
          >
            {tributeCommentsCount}
          </Typography>
        </Button>

        {/* YouTube Comments */}
        <Button
          disableElevation
          sx={buttonSx("youtube-comments")}
          onClick={() => setActiveTab("youtube-comments")}
          variant="text"
        >
          {label("Youtube Comments", "youtube-comments")}
          <Typography
            variant="body2"
            sx={{
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              color: "#fff",
              display: "grid",
              placeContent: "center",
              ml: 1,
              backgroundColor: "rgb(255, 0, 0)",
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              flexShrink: 0,
            }}
          >
            {youtubeCommentsCount}
          </Typography>
        </Button>

        {/* Contacts */}
        <Button
          disableElevation
          sx={buttonSx("contacts")}
          onClick={() => setActiveTab("contacts")}
          variant="text"
        >
          {label("Contacts", "contacts")}
          <Typography
            variant="body2"
            sx={{
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              color: "#fff",
              display: "grid",
              placeContent: "center",
              ml: 1,
              backgroundColor: colors.primary[400],
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              flexShrink: 0,
            }}
          >
            {contactsCount}
          </Typography>
        </Button>

        {/* IMAGES AND MEDIA */}
        <Button
          sx={buttonSx("media")}
          variant="text"
          onClick={() => setActiveTab("media")}
        >
          {label("Images and Media", "media")}
          <Typography
            variant="body2"
            sx={{
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              color: "#fff",
              display: "grid",
              placeContent: "center",
              ml: 1,
              backgroundColor: "#ed6c02", // Orange = unapproved/warning
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              flexShrink: 0,
            }}
          >
            {mediaCount}
          </Typography>
        </Button>

        {/* Handwritten Comments */}
        <Button
          sx={buttonSx("handwritten")}
          variant="text"
          onClick={() => setActiveTab("handwritten-comments")}
        >
          {label("Handwritten", "handwritten")}
          <Typography
            variant="body2"
            sx={{
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              color: "#fff",
              display: "grid",
              placeContent: "center",
              ml: 1,
              backgroundColor: "#9c27b0", // Purple for handwritten
              fontSize: { xs: "0.65rem", sm: "0.875rem" },
              flexShrink: 0,
            }}
          >
            {handwrittenCommentsCount}
          </Typography>
        </Button>

        {/* Final Upload */}
        <Button
          sx={buttonSx("upload")}
          variant="text"
          onClick={() => setActiveTab("upload")}
        >
          {label("Final Upload", "upload")}
        </Button>

        {/* Delivery */}
        <Button
          sx={buttonSx("delivery")}
          variant="text"
          onClick={() => setActiveTab("delivery")}
        >
          {label("Delivery", "delivery")}
        </Button>
      </Stack>
    </Box>
  );
};

TabButtons.propTypes = {
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  tributeCommentsCount: PropTypes.number.isRequired,
  youtubeCommentsCount: PropTypes.number.isRequired,
  contactsCount: PropTypes.number.isRequired,
  mediaCount: PropTypes.number.isRequired,
  handwrittenCommentsCount: PropTypes.number.isRequired,
};

export default TabButtons;